import styles from "./ValidationForm.module.scss";
import React from "react";
import {validateClassName} from "../../util/validation";
import {useCsContext} from "../../state/CsInstance";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleInfo} from "@fortawesome/sharp-solid-svg-icons/faCircleInfo";
import {observer} from "mobx-react-lite";

interface ValidationFormProps {
	setNewClass: (newClass: string) => void;
	setValidationResult: (validationResult: boolean) => void;
	onInputReceived?: () => void;
}

/**
 * Form with validation error below the input field.
 * ┌────────────────────────────────────────┐
 * │  ┌─────────────────────────────────┐   │
 * │  └─────────────────────────────────┘   │
 * │  <name> is an invalid class name       │
 * └────────────────────────────────────────┘
 */
const ValidationForm = (props: ValidationFormProps) => {
	const {codeStore} = useCsContext();

	const [newClass, setNewClass] = React.useState("");
	const [inputReceivedSinceOpening, setInputReceivedSinceOpening] =
		React.useState(false);

	const validationResult = validateClassName(
		newClass,
		codeStore.fileNames // includes hidden files, to prevent naming conflict confusion
	);
	React.useEffect(() => {
		// Allow valid or suspicious class names (sus will show a warning)
		props.setValidationResult(
			validationResult === null || validationResult.type === "suspicious"
		);
	}, [validationResult, props]);

	React.useEffect(() => {
		// reset value upon opening
		setNewClass("");
		setInputReceivedSinceOpening(false);
		// focus on the input field upon opening
		document.getElementById("classnameValidationForm")?.focus();
	}, []);

	return (
		<>
			<input
				id={"classnameValidationForm"}
				type={"text"}
				onChange={(evt) => {
					const newClassName = evt.target.value.substring(0, 255); // https://github.com/aops-ba/cs-ide/issues/749
					setNewClass(newClassName);
					props.setNewClass(newClassName);
					setInputReceivedSinceOpening(true);
					props.onInputReceived?.();
				}}
				value={newClass}
				className={[
					inputReceivedSinceOpening && validationResult?.type === "error"
						? styles.inputError
						: "",
					styles.input,
				].join(" ")}
				autoComplete="off"
			/>

			{
				// Only show the error message if it's non-empty and we've received at least some input
				// since the modal was last opened.
				validationResult && inputReceivedSinceOpening && (
					<p
						className={[
							validationResult?.type === "error"
								? styles.invalidClassNameWarning
								: styles.suspiciousClassNameWarning,
							styles.classNameWarning,
						].join(" ")}
					>
						{validationResult?.type === "suspicious" ? (
							<>
								<FontAwesomeIcon icon={faCircleInfo} />{" "}
							</>
						) : (
							""
						)}
						{validationResult?.message}
					</p>
				)
			}
		</>
	);
};

export default observer(ValidationForm);
